<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">结题审查</h1>
        <p class="text-sm text-slate-500 mt-1">终局自动化评估项目的生命周期执行完备度与核心成果真实性</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">第 5 环节</span>
        <span class="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">待开始</span>
      </div>
    </div>

    <!-- Main Content Card -->
    <div class="glass-card rounded-3xl p-6">
      <!-- Section 1: Review Dimensions -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">核心审查维度</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">选择需要审查的维度，系统将自动对结题项目进行多维度审查</p>

        <div class="ml-10" style="display: flex; flex-direction: column; gap: 20px;">
          <label class="relative cursor-pointer" @click="toggleDimension('execution')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('execution') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('execution') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('execution') ? 'text-blue-700' : 'text-slate-700'">项目执行情况审查</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('execution') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('execution')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">分析项目的任务、模板是否全部完成，考核指标是否全部达到</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('funding')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('funding') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('funding') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('funding') ? 'text-blue-700' : 'text-slate-700'">经费执行情况审查</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('funding') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('funding')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">分析项目经费是否按规定执行，检查是否有未执行经费</p>
                </div>
              </div>
            </div>
          </label>

          <label class="relative cursor-pointer" @click="toggleDimension('achievement')">
            <div class="rounded-2xl p-5 border-2 transition-all duration-300 h-full" :class="selectedDimensions.includes('achievement') ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-blue-300'">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" :class="selectedDimensions.includes('achievement') ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="font-semibold transition-colors" :class="selectedDimensions.includes('achievement') ? 'text-blue-700' : 'text-slate-700'">项目成果审查</h3>
                    <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors" :class="selectedDimensions.includes('achievement') ? 'bg-blue-500 border-blue-500' : 'border-slate-300'">
                      <svg v-if="selectedDimensions.includes('achievement')" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                  </div>
                  <p class="text-xs text-slate-500 mt-1.5 leading-relaxed">审查成果真实性、是否多头提交，保障验收成果真实可靠</p>
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>

      <!-- Section 2: Input -->
      <div class="mb-8">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">结题验收报告</h2>
        </div>
        <p class="text-sm text-slate-500 mb-4 ml-10">输入结题验收报告编号，进行结题审查</p>

        <div class="ml-10">
          <div class="relative">
            <textarea
              v-model="reportIds"
              class="premium-input w-full h-32 rounded-2xl p-5 text-sm leading-relaxed resize-none"
              placeholder="请输入结题验收报告编号，多个编号请用换行分隔..."
            ></textarea>
            <div class="absolute bottom-4 right-4 text-xs text-slate-400">
              {{ reportIds.split('\n').filter(id => id.trim()).length }} 个报告
            </div>
          </div>

          <div class="mt-4 flex items-center gap-4">
            <button class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium text-slate-600 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              批量上传
            </button>
            <span class="text-xs text-slate-400">支持 PDF、DOCX 格式</span>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="flex justify-center">
        <button class="btn-action px-10 py-4 rounded-2xl text-white font-bold text-base flex items-center gap-3">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
          </svg>
          启动结题审查
        </button>
      </div>
    </div>

    <!-- Review Summary -->
    <div class="glass-card rounded-3xl p-6">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">审查结论统计</h2>
        </div>
        <button class="btn-export px-4 py-2 rounded-xl text-white text-sm font-medium flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          导出报告
        </button>
      </div>

      <div class="grid grid-cols-3 gap-4">
        <div class="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center">
          <div class="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <div class="number-font text-3xl font-bold text-emerald-600">45</div>
          <div class="text-sm text-emerald-500 mt-1">通过验收</div>
        </div>

        <div class="p-6 bg-amber-50 border border-amber-200 rounded-2xl text-center">
          <div class="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <div class="number-font text-3xl font-bold text-amber-600">8</div>
          <div class="text-sm text-amber-500 mt-1">建议整改</div>
        </div>

        <div class="p-6 bg-red-50 border border-red-200 rounded-2xl text-center">
          <div class="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </div>
          <div class="number-font text-3xl font-bold text-red-600">2</div>
          <div class="text-sm text-red-500 mt-1">不通过</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FinalAudit',
  data() {
    return {
      selectedDimensions: ['execution', 'funding', 'achievement'],
      reportIds: ''
    }
  },
  methods: {
    toggleDimension(dimension) {
      const index = this.selectedDimensions.indexOf(dimension)
      if (index > -1) {
        this.selectedDimensions.splice(index, 1)
      } else {
        this.selectedDimensions.push(dimension)
      }
    }
  }
}
</script>
